import { useEffect, useState } from 'react';

import { differenceInMinutes } from 'date-fns';

import {
  DeliveryStatus,
  IUserOrderFragment,
  RbiOrderStatus,
  useGetUserOrdersQuery,
} from 'generated/graphql-gateway';
import { useAuthContext } from 'state/auth';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';

type IOrderInProgressRecentOrder = IUserOrderFragment | null | undefined;

export const useHasOrderNotification = () => {
  const notificationExpiredInMinutes = useFlag(LaunchDarklyFlag.TOP_ORDER_STATUS_BAR_TIME_OUT);
  const { isAuthenticated } = useAuthContext();
  const [shouldDisplayNotification, setShouldDisplayNotification] = useState<boolean>(false);
  const [currentOrderId, setCurrentOrderId] = useState<string>('');
  const [currentDeliveryStatus, setCurrentDeliveryStatus] = useState<DeliveryStatus | undefined>();

  // Start polling for recent user orders
  const { data } = useGetUserOrdersQuery({
    variables: {
      limit: 1,
      orderStatuses: [
        RbiOrderStatus.INSERT_SUCCESSFUL,
        RbiOrderStatus.UPDATE_SUCCESSFUL,
        RbiOrderStatus.REFUND_SUCCESSFUL,
      ],
    },
    skip: !isAuthenticated,
  });

  const order: IOrderInProgressRecentOrder = data?.userOrders?.orders?.[0];

  const isRefundSuccessful = order?.status === RbiOrderStatus.REFUND_SUCCESSFUL;
  const notificationIsExpired =
    order?.updatedAt &&
    Math.abs(differenceInMinutes(new Date(), new Date(order.updatedAt))) >
      notificationExpiredInMinutes;

  useEffect(() => {
    if (data) {
      const order: IOrderInProgressRecentOrder = data?.userOrders?.orders?.[0];
      if (
        order &&
        order.rbiOrderId !== currentOrderId &&
        !notificationIsExpired &&
        !isRefundSuccessful
      ) {
        setShouldDisplayNotification(true);
        setCurrentOrderId(order.rbiOrderId);
      }
    }
  }, [data, currentOrderId, notificationIsExpired, isRefundSuccessful]);

  // Update notification for delivery status changes
  useEffect(() => {
    const order: IOrderInProgressRecentOrder = data?.userOrders?.orders?.[0];
    if (
      order?.delivery?.status &&
      order.delivery.status !== currentDeliveryStatus &&
      !notificationIsExpired &&
      !isRefundSuccessful
    ) {
      setShouldDisplayNotification(true);
      setCurrentDeliveryStatus(order.delivery.status);
    }
  }, [data, currentDeliveryStatus, notificationIsExpired, isRefundSuccessful]);

  return shouldDisplayNotification;
};
